import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { ChartLineUp, Coins, Archive, ChartPieSlice, Trophy, ChartBar, Sparkle } from "@phosphor-icons/react";

// Every bar links to the catalog page filtered by the corresponding facet.
// `filterKey` names the query param the catalog will read: "materiale",
// "periodo", "rarita", "conservazione". `categoria` restricts the target
// catalog scope to a specific category (used by the per-category tabs).
const Bar = ({ row, color = "#9C5B42", filterKey, categoria }) => {
  const params = new URLSearchParams();
  if (filterKey && row.label && row.label !== "Non specificato") {
    params.set(filterKey, row.label);
  }
  const target = categoria
    ? `/catalog/${encodeURIComponent(categoria)}?${params}`
    : `/catalog/tutto?${params}`;
  const isLinkable = params.toString().length > 0;
  const Wrapper = isLinkable ? Link : "div";
  const wrapperProps = isLinkable
    ? { to: target, "data-testid": `stat-bar-${filterKey}-${row.label}` }
    : {};
  return (
    <li className="mb-3">
      <Wrapper
        {...wrapperProps}
        className={`block ${isLinkable ? "hover:bg-[#F0EBE1] -mx-2 px-2 py-1 rounded-sm transition-colors" : ""}`}
      >
        <div className="flex justify-between mb-1.5 text-sm">
          <span className="text-[#1E2520]">{row.label}</span>
          <span className="text-[#5C635A] font-mono">
            {row.count} · {row.percent}% {row.valore > 0 && `· € ${Number(row.valore).toLocaleString("it-IT")}`}
          </span>
        </div>
        <div className="h-2 bg-[#E5E0D8]">
          <div className="h-full transition-all" style={{ width: `${Math.min(100, row.percent)}%`, backgroundColor: color }} />
        </div>
      </Wrapper>
    </li>
  );
};

const Section = ({ icon: Icon, title, hint, children }) => (
  <div className="card p-6 md:p-8 mb-6">
    <div className="flex items-center gap-2 label-eyebrow mb-1">
      <Icon weight="duotone" /> {title}
    </div>
    {hint && <p className="text-xs text-[#5C635A] mb-4">{hint}</p>}
    {!hint && <div className="mb-3" />}
    {children}
  </div>
);

// Material colour palette shared across the two "per materiale" charts.
const materialColour = (label = "") => {
  const lc = label.toLowerCase();
  if (lc.includes("oro")) return "#C9A227";
  if (lc.includes("argento")) return "#9AA0A6";
  if (lc.includes("bronzo")) return "#A0723E";
  if (lc.includes("rame")) return "#B87333";
  if (lc.includes("ottone")) return "#B5A642";
  if (lc.includes("ferro")) return "#5C5A56";
  if (lc.includes("piombo")) return "#6B7079";
  return "#9C5B42";
};

// Top-of-page KPI cards. Rendered both in the "Tutto" tab and in each
// category tab (with category-specific totals).
const KpiRow = ({ total, valoreTotale, valoreMedio, labelTotal = "Totale reperti" }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
    <div className="card p-6 md:p-8">
      <div className="flex items-center gap-2 label-eyebrow mb-2"><Archive weight="duotone" /> {labelTotal}</div>
      <div className="font-serif text-5xl text-[#1E2520]">{total}</div>
    </div>
    <div className="card p-6 md:p-8">
      <div className="flex items-center gap-2 label-eyebrow mb-2"><Coins weight="duotone" /> Valore totale</div>
      <div className="font-serif text-5xl text-[#9C5B42]">
        € {Number(valoreTotale || 0).toLocaleString("it-IT")}
      </div>
    </div>
    <div className="card p-6 md:p-8">
      <div className="flex items-center gap-2 label-eyebrow mb-2"><ChartLineUp weight="duotone" /> Valore medio</div>
      <div className="font-serif text-5xl text-[#1E2520]">
        € {Number(valoreMedio || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}
      </div>
    </div>
  </div>
);

// Compact list of top-N items for a category (or the global top).
const TopList = ({ items }) => (
  <ul className="space-y-3">
    {items.map((it, i) => (
      <li key={it.id}>
        <Link
          to={`/artifact/${it.id}`}
          className="flex items-center gap-4 p-3 hover:bg-[#F0EBE1] transition-colors rounded-sm"
          data-testid={`top-${i}`}
        >
          <span className="font-serif text-2xl text-[#9C5B42] w-6">{i + 1}</span>
          {it.photos?.[0] && (
            <img src={fileUrl(it.photos[0])} alt="" className="w-14 h-14 object-cover bg-[#E5E0D8]" />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {it.inventory_code && (
                <span className="bg-[#1E2520] text-[#F9F8F6] font-mono text-xs px-2 py-0.5 rounded-sm">
                  {it.inventory_code}
                </span>
              )}
              <span className="text-[#1E2520] truncate">{it.identification?.nome}</span>
            </div>
            <div className="text-xs text-[#5C635A]">{it.identification?.categoria}</div>
          </div>
          <span className="font-serif text-xl text-[#9C5B42] whitespace-nowrap">
            € {Number(it.identification?.valore_stimato_eur || 0).toLocaleString("it-IT")}
          </span>
        </Link>
      </li>
    ))}
  </ul>
);

export default function Stats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("__all__");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/stats");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const currentCategory = useMemo(() => {
    if (!data || activeTab === "__all__") return null;
    return (data.per_categoria_details || []).find((c) => c.categoria === activeTab);
  }, [activeTab, data]);

  if (loading || !data) {
    return (
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-14">
        <div className="h-12 w-2/3 scanning mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => <div key={i} className="card aspect-[3/2] scanning" />)}
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "__all__", label: "Tutto", count: data.totale_reperti },
    ...((data.per_categoria_details || []).map((c) => ({
      id: c.categoria,
      label: c.categoria,
      count: c.count,
    }))),
  ];

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-10 md:py-14 fade-in">
      <div className="label-eyebrow mb-3">Statistiche</div>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#1E2520] mb-4">
        Una panoramica del tuo museo
      </h1>
      <p className="text-sm text-[#5C635A] mb-8">
        Clicca su qualsiasi barra per aprire il catalogo filtrato per quella voce.
      </p>

      {/* Category tabs — horizontal scrollable on mobile */}
      <div className="flex gap-2 mb-8 overflow-x-auto pb-2 -mx-1 px-1" data-testid="stats-tabs">
        {tabs.map((t) => {
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border transition-colors whitespace-nowrap ${
                active
                  ? "bg-[#9C5B42] border-[#9C5B42] text-white"
                  : "country-tab-inactive"
              }`}
              data-testid={`stats-tab-${t.id}`}
            >
              {t.label} <span className="opacity-70 font-mono">{t.count}</span>
            </button>
          );
        })}
      </div>

      {activeTab === "__all__" ? (
        <>
          <KpiRow
            total={data.totale_reperti}
            valoreTotale={data.valore_totale_eur}
            valoreMedio={data.valore_medio_eur}
          />

          {data.per_categoria?.length > 0 && (
            <Section icon={ChartPieSlice} title="Per categoria" hint="Ogni barra ti porta al catalogo filtrato per quella categoria.">
              <ul>
                {data.per_categoria.map((r) => (
                  <Link
                    key={r.label}
                    to={`/catalog/${encodeURIComponent(r.label)}`}
                    className="block hover:bg-[#F0EBE1] -mx-2 px-2 py-1 rounded-sm transition-colors"
                    data-testid={`stat-bar-categoria-${r.label}`}
                  >
                    <div className="flex justify-between mb-1.5 text-sm">
                      <span className="text-[#1E2520]">{r.label}</span>
                      <span className="text-[#5C635A] font-mono">
                        {r.count} · {r.percent}% {r.valore > 0 && `· € ${Number(r.valore).toLocaleString("it-IT")}`}
                      </span>
                    </div>
                    <div className="h-2 bg-[#E5E0D8]">
                      <div className="h-full transition-all" style={{ width: `${Math.min(100, r.percent)}%`, backgroundColor: "#9C5B42" }} />
                    </div>
                  </Link>
                ))}
              </ul>
            </Section>
          )}

          {data.per_materiale?.length > 0 && (
            <Section icon={ChartBar} title="Per materiale">
              <ul>{data.per_materiale.map((r) => <Bar key={r.label} row={r} color={materialColour(r.label)} filterKey="materiale" />)}</ul>
            </Section>
          )}

          {data.per_periodo?.length > 0 && (
            <Section icon={ChartBar} title="Per periodo">
              <ul>{data.per_periodo.map((r) => <Bar key={r.label} row={r} color="#7A4632" filterKey="periodo" />)}</ul>
            </Section>
          )}

          {data.per_rarita?.length > 0 && (
            <Section icon={Sparkle} title="Per rarità">
              <ul>{data.per_rarita.map((r) => <Bar key={r.label} row={r} color="#4A5043" filterKey="rarita" />)}</ul>
            </Section>
          )}

          {data.per_conservazione?.length > 0 && (
            <Section icon={ChartBar} title="Per stato di conservazione">
              <ul>{data.per_conservazione.map((r) => <Bar key={r.label} row={r} color="#5C635A" filterKey="conservazione" />)}</ul>
            </Section>
          )}

          {data.top_valore?.length > 0 && (
            <Section icon={Trophy} title="I 5 reperti più preziosi (tutto il catalogo)">
              <TopList items={data.top_valore} />
            </Section>
          )}
        </>
      ) : currentCategory ? (
        <>
          <KpiRow
            total={currentCategory.count}
            valoreTotale={currentCategory.valore_totale}
            valoreMedio={currentCategory.valore_medio}
            labelTotal={`Reperti in "${currentCategory.categoria}"`}
          />

          {currentCategory.per_materiale?.length > 0 && (
            <Section icon={ChartBar} title="Per materiale" hint="Clicca per vedere i reperti di questa categoria in quel materiale.">
              <ul>
                {currentCategory.per_materiale.map((r) => (
                  <Bar key={r.label} row={r} color={materialColour(r.label)} filterKey="materiale" categoria={currentCategory.categoria} />
                ))}
              </ul>
            </Section>
          )}

          {currentCategory.per_periodo?.length > 0 && (
            <Section icon={ChartBar} title="Per periodo">
              <ul>
                {currentCategory.per_periodo.map((r) => (
                  <Bar key={r.label} row={r} color="#7A4632" filterKey="periodo" categoria={currentCategory.categoria} />
                ))}
              </ul>
            </Section>
          )}

          {currentCategory.per_rarita?.length > 0 && (
            <Section icon={Sparkle} title="Per rarità">
              <ul>
                {currentCategory.per_rarita.map((r) => (
                  <Bar key={r.label} row={r} color="#4A5043" filterKey="rarita" categoria={currentCategory.categoria} />
                ))}
              </ul>
            </Section>
          )}

          {currentCategory.per_conservazione?.length > 0 && (
            <Section icon={ChartBar} title="Per stato di conservazione">
              <ul>
                {currentCategory.per_conservazione.map((r) => (
                  <Bar key={r.label} row={r} color="#5C635A" filterKey="conservazione" categoria={currentCategory.categoria} />
                ))}
              </ul>
            </Section>
          )}

          {currentCategory.top_valore?.length > 0 && (
            <Section icon={Trophy} title={`I 3 più preziosi di "${currentCategory.categoria}"`}>
              <TopList items={currentCategory.top_valore} />
            </Section>
          )}
        </>
      ) : (
        <div className="text-center text-[#5C635A] py-10">Nessun dato per questa categoria.</div>
      )}

      {data.totale_reperti === 0 && (
        <div className="text-center text-[#5C635A] py-10">Nessun dato ancora. Identifica il tuo primo reperto!</div>
      )}
    </div>
  );
}
